import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { Formik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { prefixFilter } from '@utils/filter.utils'
import { TransactionType } from '@utils/transaction.utils'
import { isZero } from '@utils/validations'

import {
  isPaymentProviderBankType,
  isPaymentProviderCardProviderType,
  isPaymentProviderCryptoType,
  isPaymentProviderNoneType,
  isPaymentProviderPSPType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'
import { TradingAccountDetailDto } from '@model/TradingAccountDetailDto'
import { TransactionStatus } from '@model/TransactionStatus'
import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { safeLocalStorage } from '@hooks/useStorage'

import { FundingTemplate } from '@shared/templates/FundingTemplate'

import { THREE_DS_FORM_VALUES_STORAGE_KEY } from '../constants/threeDS'
import { getNormalizedWallet, isChinaUnionPayPSP } from '../helpers'
import { DepositTarget } from '../hooks/types'
import { useGetDepositCurrencyLimits } from '../hooks/useGetDepositCurrencyLimits'
import { buildDepositInitialValues, isRealPspId, validatePaymentProviderFields } from './helpers'
import { useDepositDestination } from './hooks/useDepositDestination'
import { useDepositPaymentProvider } from './hooks/useDepositPaymentProvider'
import { useDepositProviderCache } from './hooks/useDepositProviderCache'
import { useDepositUrlProvider } from './hooks/useDepositUrlProvider'
import type { DepositFlowLocationState, DepositFormValues } from './types'

interface DepositFlowLayoutPageProps {
  target?: DepositTarget
  targetId?: string
  dashboardContext?: 'traders-room' | 'payment-agent'
}

export const DepositFlowLayoutPage: React.FC<DepositFlowLayoutPageProps> = (props) => {
  const { target: targetProp, targetId: targetIdProp, dashboardContext } = props
  const resolvedDashboardContext = dashboardContext ?? 'traders-room'

  const { t } = useTranslation()
  const navigate = useNavigate()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { account } = useAccountReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()
  const isCFDProduct = isDefaultCFDProductType()

  const location = useLocation()
  const locationState = useMemo(
    () => (location.state || {}) as DepositFlowLocationState,
    [location.state]
  )

  const params = useParams<{
    target: DepositTarget
    id: string
    walletId?: string
    pspId?: string
  }>()

  const target = targetProp ?? params.target
  const id = targetIdProp ?? params.id ?? params.walletId
  const pspId = params.pspId

  const { walletPaymentProvider, selectionState, setWalletPaymentProvider } =
    useDepositProviderCache({
      target,
      id,
      pspId,
      locationState,
    })

  const { destination, destinationLoading, walletIdForProvider } = useDepositDestination({
    target,
    id,
  })

  const { isUrlProviderLoading, urlProviderResolved } = useDepositUrlProvider({
    target,
    id,
    pspId,
    walletIdForProvider,
    destinationLoading,
    walletPaymentProvider,
    setWalletPaymentProvider,
    locale,
  })

  const handleBack = useCallback(() => {
    if (!target) return
    navigate(`/dashboard/${resolvedDashboardContext}/deposit/${target}`, {
      replace: true,
      state: selectionState,
    })
  }, [navigate, resolvedDashboardContext, selectionState, target])

  const { paymentProvider, providerLoading } = useDepositPaymentProvider({
    walletPaymentProvider,
    walletIdForProvider,
    locale,
    onBack: handleBack,
  })

  const destinationWalletTypeId = useMemo(() => {
    if (target !== 'wallet' || !destination) return undefined
    return 'walletType' in destination ? destination?.walletType?.id : ''
  }, [destination, target])

  const isETDWallet =
    !isCFDProduct && target === 'wallet' && destinationWalletTypeId === WalletTypeEnum.ETD

  const isETDTransactionsEnabled = useMemo(() => {
    if (isCFDProduct || target !== 'wallet') return false
    const walletDestination = target === 'wallet' ? destination : undefined
    if (
      walletDestination &&
      'walletType' in walletDestination &&
      walletDestination?.walletType?.id !== WalletTypeEnum.ETD
    ) {
      return false
    }
    return true
  }, [isCFDProduct, target, destination])

  const { data: etdTransactions, isLoading: etdTransactionsLoading } = useQuery({
    queryKey: queryKeys.deposit.flowTransactions({
      accountId: account?.id,
      target,
      destinationWalletTypeId,
    }),
    queryFn: () =>
      apiClient.getTransactions({
        caller: 'ca',
        pageSize: 1,
        ca_search_ClientWalletType: prefixFilter({
          prefix: account?.id,
          value: WalletTypeEnum.ETD,
        }),
        ca_search_TransactionTypeId: TransactionType.WalletDeposit,
        ca_search_TransactionStateId: TransactionStatus.Completed,
      }),
    enabled: isETDTransactionsEnabled,
  })

  const isETDFirstTimeDeposit =
    isETDWallet && etdTransactions ? (etdTransactions?.items?.length ?? 0) === 0 : false

  const isCampaignTADepositsQueryEnabled = useMemo(() => {
    if (target !== 'trading-account' || !destination) return false
    const ta = destination as TradingAccountDetailDto
    return !!ta.campaign?.id && ta.accountGroup?.accountGroupType?.name !== 'Demo'
  }, [destination, target])

  const { data: campaignTATransactions, isLoading: campaignTATransactionsLoading } = useQuery({
    queryKey: queryKeys.deposit.flowTransactions({
      accountId: id,
      target: 'trading-account',
      campaignId: (destination as TradingAccountDetailDto)?.campaign?.id,
    }),
    queryFn: () =>
      apiClient.getTransactions({
        caller: 'ca',
        pageSize: 1,
        ca_search_TaId: id,
        ca_search_TransactionTypeId: TransactionType.TransferToTradingAccount,
        ca_search_TransactionStateId: TransactionStatus.Completed,
      }),
    enabled: isCampaignTADepositsQueryEnabled,
  })

  const isFirstDepositToCampaignTA =
    isCampaignTADepositsQueryEnabled && campaignTATransactions
      ? (campaignTATransactions?.items?.length ?? 0) === 0
      : false

  const needsUrlProviderResolution = isRealPspId(pspId)
  const isActuallyLoading =
    destinationLoading ||
    providerLoading ||
    isUrlProviderLoading ||
    etdTransactionsLoading ||
    campaignTATransactionsLoading ||
    (needsUrlProviderResolution ? !urlProviderResolved : false)

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const isThreeDSFailure = searchParams.get('3ds') === 'failure'

  const restoredValues = useMemo(() => {
    if (!isThreeDSFailure || !id) return undefined
    const stored = safeLocalStorage.getJSON<DepositFormValues>(THREE_DS_FORM_VALUES_STORAGE_KEY)
    if (!stored) return undefined

    const storedId = stored.wallet?.id || stored.tradingAccount?.id
    if (storedId !== id) return undefined

    return stored
  }, [id, isThreeDSFailure])

  const { formatMoney } = useFormatNumber()
  const { getDepositCurrencyLimits } = useGetDepositCurrencyLimits()

  const initialValues = useMemo(() => {
    if (!paymentProvider || !destination || !target) return null
    return buildDepositInitialValues({
      target,
      destination: destination,
      paymentProvider,
      dashboardContext: resolvedDashboardContext,
      restoredValues,
    })
  }, [destination, paymentProvider, resolvedDashboardContext, restoredValues, target])

  if (isActuallyLoading) {
    return <FundingTemplate isLoading onBack={handleBack} />
  }

  if (
    !target ||
    !id ||
    !walletPaymentProvider ||
    !destination ||
    !paymentProvider ||
    !initialValues
  ) {
    return null
  }

  return (
    <Formik<DepositFormValues>
      initialValues={initialValues}
      enableReinitialize
      validate={async (values) => {
        const errors: any = {}

        const categoryId = values.paymentProvider?.providerCategory?.id
        const providerCurrencyId = values.paymentProvider?.currency?.id

        const setNestedError = (path: string, message: string) => {
          const keys = path.split('.')
          let cur = errors
          for (let i = 0; i < keys.length - 1; i++) {
            const k = keys[i]
            cur[k] = cur[k] || {}
            cur = cur[k]
          }
          cur[keys[keys.length - 1]] = message
        }

        const rawAmount = values.amount
        const amount = rawAmount === null || rawAmount === undefined ? undefined : Number(rawAmount)
        const hasValidAmount = typeof amount === 'number' && Number.isFinite(amount)

        const shouldCheckProviderLimits =
          isPaymentProviderPSPType(categoryId) ||
          isPaymentProviderCardProviderType(categoryId) ||
          isPaymentProviderBankType(categoryId) ||
          isPaymentProviderCryptoType(categoryId)

        const shouldApplyFallbackMin =
          !isPaymentProviderCardProviderType(categoryId) &&
          !isPaymentProviderPaymentAgentType(categoryId)

        const fieldErrors = validatePaymentProviderFields(
          values.paymentProvider?.parameters?.form || [],
          values.fields,
          t
        )
        if (Object.keys(fieldErrors).length > 0) {
          errors.fields = fieldErrors
        }

        if (values.tradingAccount?.isDemo) {
          return errors
        }
        if (!isPaymentProviderPaymentAgentType(categoryId)) {
          if (amount === undefined || !hasValidAmount) {
            errors.amount = t('Enter an amount')
          } else if (amount <= 0 || isZero(amount)) {
            errors.amount = t('Amount must be greater than zero')
          }

          const canValidateAmountLimits = !errors.amount && hasValidAmount && amount !== undefined

          if (canValidateAmountLimits && shouldCheckProviderLimits) {
            const walletForLimits = getNormalizedWallet(values)
            if (!walletForLimits) return errors

            const limits = await getDepositCurrencyLimits(walletForLimits, values.paymentProvider)

            const minLimit = limits?.minAmount ?? null
            const maxLimit = limits?.maxAmount ?? null

            const hasMinLimit =
              typeof minLimit === 'number' && Number.isFinite(minLimit) && minLimit > 0
            const hasMaxLimit =
              typeof maxLimit === 'number' && Number.isFinite(maxLimit) && maxLimit > 0

            if (hasMinLimit && amount < minLimit) {
              errors.amount = t('Method minimum is {{amount}}', {
                amount: formatMoney(minLimit, providerCurrencyId || 'USD'),
              })
            } else if (hasMaxLimit && amount > maxLimit) {
              errors.amount = t('Method maximum is {{amount}}', {
                amount: formatMoney(maxLimit, providerCurrencyId || 'USD'),
              })
            } else if (!hasMinLimit && shouldApplyFallbackMin) {
              const isUnionPay = isChinaUnionPayPSP(values.paymentProvider?.id)

              const defaultMinAmount = isUnionPay ? 100 : 1000
              const defaultMinCurrency = isUnionPay ? 'CNY' : 'USD'

              const currencyMin = values.paymentProvider?.currency?.minAmount
              const hasCurrencyMin =
                typeof currencyMin === 'number' && Number.isFinite(currencyMin) && currencyMin > 0

              const violatesFallbackMin = hasCurrencyMin
                ? amount < currencyMin!
                : providerCurrencyId === defaultMinCurrency && amount < defaultMinAmount

              const etdException = isETDWallet && isETDFirstTimeDeposit

              if (violatesFallbackMin && !etdException) {
                if (hasCurrencyMin && providerCurrencyId) {
                  errors.amount = t('Method minimum is {{amount}}', {
                    amount: formatMoney(currencyMin!, providerCurrencyId),
                  })
                } else if (providerCurrencyId === defaultMinCurrency) {
                  errors.amount = t('Method minimum is {{amount}}', {
                    amount: formatMoney(defaultMinAmount, defaultMinCurrency),
                  })
                }
              }
            }
          }

          if (hasValidAmount && amount && values.isBonusSelected && values.bonusCampaignId) {
            const destinationCurrencyId =
              values?.wallet?.currency?.id || values?.tradingAccount?.currency?.id
            const campaigns = values.availableBonusCampaigns || []

            const selectedCampaign = campaigns.find(
              (c) => c.bonusCampaign?.id === values.bonusCampaignId
            )

            if (selectedCampaign) {
              const minDepositAmount = selectedCampaign.minDepositAmount ?? 0

              const bonusDepositAmount =
                values.paymentProvider?.currency?.id === destinationCurrencyId
                  ? amount
                  : values.conversionRate
                  ? Math.round(amount * values.conversionRate * 100) / 100
                  : amount

              if (bonusDepositAmount < minDepositAmount) {
                errors.bonusAmount = t('Wallet.Minimum deposit amount for this bonus is', {
                  minAmount: formatMoney(minDepositAmount, destinationCurrencyId || 'USD'),
                })
              }
            }
          }
        }

        if (!isPaymentProviderNoneType(categoryId)) {
          if (!values.terms?.paymentOperations) {
            setNestedError('terms.paymentOperations', t('Validation.Required'))
          }

          if (isPaymentProviderPaymentAgentType(categoryId)) {
            if (!values.terms?.powerOfAttorney) {
              setNestedError('terms.powerOfAttorney', t('Validation.Required'))
            }
          }
        }

        if (isChinaUnionPayPSP(values.paymentProvider?.id) && !values.terms?.cupResponsibility) {
          setNestedError('terms.cupResponsibility', t('Validation.Required'))
        }

        if (values.comment && values.comment.length > 100) {
          errors.comment = t('Validation.The character limit is 100 characters')
        }
        if (
          values.amount &&
          values.tradingAccount?.campaign?.tradingAccountCurrencyMinDepositAmount
        ) {
          const minDeposit = values.tradingAccount.campaign.tradingAccountCurrencyMinDepositAmount

          if (isFirstDepositToCampaignTA && values.amount < minDeposit) {
            errors.amount = t('Wallet.Below minimum deposit for campaign', {
              amount: formatMoney(minDeposit, values.tradingAccount?.currency?.id),
            })
          }
        }

        return errors
      }}
      onSubmit={() => undefined}
    >
      <Outlet />
    </Formik>
  )
}
